import dayjs from 'dayjs';
import WeekPicker from './datepicker';
import { useParams } from 'react-router-dom';
import constants from '../../../../constants';
import AlertContext from '../../../../context/AlertContext';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useState, useEffect, useContext } from 'react';
import useAxiosPrivate from '../../../../Hooks/useAxiosPrivate';
import useCommonFunctionHooks from '../../../../Hooks/useCommonFunctionHooks';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

const AttendanceChart = () => {
  const [month, setMonth] = useState(dayjs().format('MMMM'));
  const [open, setOpen] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(dayjs());
  const axios = useAxiosPrivate();
  const Alert = useContext(AlertContext);
  const getCatchBlockDetails = useCommonFunctionHooks;
  const { id } = useParams();
  const [chartData, setChartData] = useState([]);
  const [sessionDates, setSessionDates] = useState([]); // New state for session dates
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleAttendanceDetail = () => {
      setIsLoading(true);
      const startOfWeek = selectedWeek.startOf('week').format('YYYY-MM-DD');
      const endOfWeek = selectedWeek.endOf('week').format('YYYY-MM-DD');
      
      axios.get(`node/admin/batch/db/s_attendance`, {
        params: {
          batchid: id,
          startdate: startOfWeek,
          enddate: endOfWeek
        },
        headers: {
          "Content-type": "application/json",
        },
      })
      .then((res) => {
        if (res.data.resultCode === constants.RESULT_STATUS.TECHNICAL_ERROR) {
          Alert.setShowNotify({
            show: true,
            title: "Info",
            msg: res.data.msg,
          });
        } else {
          // Store session dates
          setSessionDates(res.data.data.sDates || []);

          const startDate = dayjs(startOfWeek);
          const totalDays = 7;
          const rawData = res.data.data.sessionAttendance || [];

          const formattedData = [];

          for (let i = 0; i < totalDays; i++) {
            const currentDate = startDate.add(i, 'day');

            const dataForDay = rawData.find((item) =>
              dayjs(item.session_date).isSame(currentDate, 'day')
            );

            if (dataForDay) {
              let sessions = rawData
              .filter((s) => dayjs(s.session_date).isSame(currentDate, 'day'))
              .map((s) => ({
                    total: s.total_invited || 0,
                    present: s.present_count || 0,
                    absent: s.absent_count || 0,
              }));
              formattedData.push({
                day: currentDate.format('DD'),
                attendance: dataForDay.attendancepercent,
                date: currentDate.format('DD MMM YYYY'),
                sessions: sessions,
                });
            } else {
              formattedData.push({
                day: currentDate.format('DD'),
                attendance: null,
                date: currentDate.format('DD MMM YYYY'),
                sessions: [],
              });
            }
          }
          setChartData(formattedData);
        }
      })
      .catch((error) => {
        getCatchBlockDetails(error);
      })
      .finally(() => setIsLoading(false));
    };

    handleAttendanceDetail();
  }, [selectedWeek]);

  return (
    <div className="attendance-chart-container">
      <div className="header">
        <span className='head-name'> Attendance</span>
        <WeekPicker
          month={`${selectedWeek.startOf('week').format('Do MMM')} - ${selectedWeek.endOf('week').format('Do MMM')}`}
          setMonth={setMonth}
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          sessionDates={sessionDates} // Pass session dates to WeekPicker
        />
      </div>
      
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </div>
      ) : (
        <div className="line-chart-container">
          <ResponsiveContainer  height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 20 ,right:20}}
            >
              
              <XAxis
              dataKey="day"
              axisLine={{stroke:'#f0f0fo'}}
              tickLine={false}
              tick={{ fill: '#9ca3af', fontSize: 10 }}
              padding={{top:8}}
              />
              
              <YAxis
                tick={{ fill: '#9ca3af', fontSize: 10 }}
                axisLine={{ stroke: '#f0f0f0' }}
                tickLine={false}
                domain={[0, 100]}
                // tickMargin={5}
                ticks={[0, 20, 40, 60, 80, 100]}
              />

              <CartesianGrid 
                strokeDasharray= "3 3"
                stroke="#F1F1F1" 
                vertical={true}
              />
              {[0, 20, 40, 60, 80, 100].map(tick => (
                <ReferenceLine
                  key={tick}
                  y={tick}
                  stroke='#F1F1F1'
                  strokeDasharray="3 3"
                />
              ))}
            
              <Tooltip
                  cursor={false}
                  wrapperStyle={{ marginTop: 25 }}
                  content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="linechart-tooltip-container">
                        <p className="linechart-tooltip-date">{data.date}</p>
                        <div className="linechart-tooltip-sessions">
                          {data.sessions && data.sessions.length > 0 && (
                            data.sessions.map((s, index) => (
                              <div key={index} className='linechart-tooltip-session'>
                                <p className="linechart-tooltip-session-name">Session {index + 1}</p>
                                <p className="linechart-tooltip-total">Total: {s.total} Students</p>
                                <p className="linechart-tooltip-present">
                                <span className="linechart-tooltip-present-label">Present: </span>
                                {s.present} Students
                                </p>
                                <p className="linechart-tooltip-absent">
                                  <span className="linechart-tooltip-absent-label">Absent: </span>
                                  {s.absent} Students</p>
                              </div>
                          )))}
                        </div>
                        </div>
                      );
                  }
                    return null;
                 }}
              />
              <Line
                type="monotone"
                dataKey="attendance"
                stroke="#ff6b6b"
                strokeWidth={1}
                connectNulls={true}
                dot={({ cx, cy, payload }) =>
                  payload.attendance != null ? (
                    <circle r={2} cx={cx} cy={cy} fill="#fff" stroke="#ff6b6b" strokeWidth={1.2} />
                  ) : null
                }
                activeDot={({ cx, cy, payload }) =>
                  payload.attendance != null ? (
                    <circle r={3.5} cx={cx} cy={cy} fill="#fff" stroke="#ff6b6b" strokeWidth={1.3} />
                  ) : null
                }
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default AttendanceChart;